// pages/DashboardV2.jsx
// SurFox "Leads" — attention dashboard.
// Two modes under one page, switched by the LeadsTabs control:
//   • Attention  — three collapsible activity sections (Needs you now / Active
//                  conversations / Quiet). Every row leads with the WHY + one action.
//   • Keep warm   — the "engaged, not a fit yet" pool (any industry), with
//                  AI-derived "looking for" chips + location. Grouped by
//                  specialty for Staffing tenants, by campaign for everyone else.
//
// Data layer (fetchLeads, tenant scoping, global-admin view, the section
// partition) is preserved from the prior DashboardV2; only the presentation and
// the bench fields (fit_for, bench_location, mismatch_details) are new.
//
// Requires migration 20260610_add_bench_enrichment.sql (fit_for, bench_location,
// bench_enriched_at) and the enrich-bench edge function.

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AddLead from '../pages/AddLead';
import supabase from '../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../lib/tenantVisibility';
import {
  Search, Plus, Phone, Calendar, MessageSquare, Flame, Clock, RefreshCw,
  ChevronDown, ArrowRight, Users, Pin, AlertCircle, X, Loader2, Globe,
  Zap, Target, Bell, Moon,
} from 'lucide-react';

// ============================================================================
// CONSTANTS
// ============================================================================
const ACTIVITY_WINDOW_DAYS = 7;
const QUIET_ARCHIVE_DAYS = 21;            // Quiet leads silent longer than this are hidden (and archived by the nudge cron — see INTEGRATION.md)
// "Lead replied, no answer yet" only fires once an inbound has gone unanswered this
// long AND we're currently inside business hours, so normal reply latency and
// after-hours / weekend holds never trip it. Window is the viewer's local time;
// swap for the tenant's configured messaging hours if per-tenant accuracy is needed.
const REPLY_STALE_HOURS = 2;
const BUSINESS_START_HOUR = 8;   // 8am local
const BUSINESS_END_HOUR = 20;    // 8pm local

// Why the AI is off, when the lead row actually records it. Some shutoff paths still
// write nothing (quality-monitor kill, manual toggles predating the LeadDetail fix), so
// we fall back to the lead's last classified intent to tell "stopped on purpose" from
// "stopped by mistake". As of 2026-09-09 an AI close records WHICH verdict closed it
// rather than the old catch-all; `disqualified_ai` is retained for the ~700 rows written
// before that and is no longer produced.
const DISPOSITION_LABELS = {
  manual_off: 'AI turned off by a rep',
  opted_out: 'Lead opted out',
  disqualified_ai: 'SurFox disqualified this lead',
  permanent_disqualifier: 'No longer eligible',
  wrong_number: 'Wrong number',
  incoherent: 'Unreadable replies',
  explicit_decline: 'Lead declined',
  followup_exhausted: 'Follow-ups exhausted',
  ghosted_no_reply: 'Never replied',
};
// Last classified intent that makes an AI shutoff EXPECTED, keyed to the real
// classifyintent vocabulary. Anything else (POSITIVE, NEUTRAL, INFO_REQUEST,
// FUTURE_INTENT) means the AI went quiet on a lead that had NOT disengaged, which is
// the shape of an accidental shutoff and is worth a human glance. FUTURE_INTENT is
// deliberately excluded: killing the AI on a "contact me later" lead strands it.
const INTENT_OFF_LABELS = {
  NOT_INTERESTED: 'SurFox stopped, not interested',
  OPT_OUT: 'SurFox stopped, opted out',
  WRONG_NUMBER: 'SurFox stopped, wrong number',
  PERMANENT_DISQUALIFIER: 'SurFox stopped, disqualified',
  CONVERSATION_ENDING: 'SurFox stopped, lead ended it',
  OFFER_DECLINED: 'SurFox stopped, offer declined',
};
const DISENGAGED_INTENTS = new Set(Object.keys(INTENT_OFF_LABELS));
const TERMINAL_STATUSES = ['Won', 'Lost', 'Opted Out', 'dead', 'Disqualified', 'Converted'];
const SECTION_PAGE_SIZE = 25;
// Tenant-admin roles see every lead in their tenant (same as business_admin).
// Only the limited 'user' (sales rep) role is scoped to its sales_team assignments.
const TENANT_ADMIN_ROLES = ['business_admin', 'partner_admin', 'co_admin'];

// Reason vocabulary — the "why" a lead is on your plate.
const REASON = {
  meeting:    { Icon: Calendar,      cls: 'bg-emerald-50 text-emerald-600' },
  question:   { Icon: MessageSquare, cls: 'bg-cyan-50 text-cyan-600' },
  hot:        { Icon: Flame,         cls: 'bg-orange-50 text-orange-600' },
  intent:     { Icon: Target,        cls: 'bg-indigo-50 text-indigo-600' },
  unanswered: { Icon: Bell,          cls: 'bg-amber-50 text-amber-600' },
  cooling:    { Icon: Clock,         cls: 'bg-slate-100 text-slate-400' },
  snoozed:    { Icon: Moon,          cls: 'bg-violet-50 text-violet-500' },
};

// Staffing specialty → human group label for the bench.
const SPECIALTY_LABELS = {
  rn: 'Registered Nurses', aprn: 'Nurse Practitioners (APRN)', lpn: 'LPN / LVN',
  cna: 'CNA', medical_assistant: 'Medical Assistants', physician: 'Physicians',
  obgyn: 'OB/GYN Physicians', therapist: 'Therapists (PT/OT/SLP)', rad_tech: 'Radiology Techs',
  lcsw: 'Clinical Social Workers', psych: 'Behavioral Health', healthcare_other: 'Other Healthcare',
  healthcare: 'Healthcare', tech: 'Tech', industrial: 'Industrial', executive: 'Executive',
};
// Map a specialty value to a group label. Canonical keys use the table above;
// free-text values (newer surfAgent campaigns) are Title-Cased rather than dumped to "Other".
const specialtyLabel = (s) => {
  if (!s) return 'Other';
  if (SPECIALTY_LABELS[s]) return SPECIALTY_LABELS[s];
  return String(s).replace(/\b\w/g, (c) => c.toUpperCase());
};

// ============================================================================
// HELPERS
// ============================================================================
// messages.timestamp is UTC-naive (no tz marker, and the RPC may use a space
// separator). Bare new Date() parses that as LOCAL time, which makes every message
// look newer than it is by the viewer's UTC offset — under-reporting every wait and
// defeating the REPLY_STALE_HOURS gate. Normalize to UTC, same as LeadDetail does.
const asDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const s = String(v).replace(' ', 'T');
  return new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(s) ? s : s + 'Z');
};
const asMs = (v) => { const d = asDate(v); return d ? d.getTime() : 0; };

// Tenant timezone (platform_settings key 'timezone'). Mirrors _shared/timezone.ts so
// the dashboard reads local hours the same way scoring does. Everything time-of-day
// (business-hours gate, appointment display, "today") resolves in THIS zone, never the
// viewer's browser zone.
const TZ_ABBR = {
  EST: 'America/New_York', EDT: 'America/New_York', CST: 'America/Chicago', CDT: 'America/Chicago',
  MST: 'America/Denver', MDT: 'America/Denver', PST: 'America/Los_Angeles', PDT: 'America/Los_Angeles',
  HST: 'Pacific/Honolulu', AKST: 'America/Anchorage', AKDT: 'America/Anchorage',
};
const toIANA = (tz) => tz ? (TZ_ABBR[String(tz).toUpperCase()] || tz) : 'America/New_York';
// Calendar-day key (YYYY-MM-DD) for a Date as seen in a given IANA zone.
const dayKeyInTz = (d, tz) => {
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d); }
  catch { return d.toDateString(); }
};

function formatRelativeTime(iso) {
  if (!iso) return null;
  const ms = Date.now() - asMs(iso);
  if (ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo`;
  return `${Math.floor(mo / 12)}y`;
}
const daysSince = (iso) => iso ? Math.floor((Date.now() - asMs(iso)) / 86400000) : 0;
const initials = (n) => (n || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

// Is "now" within business hours in the viewer's local timezone? Weekends and
// nights count as legitimate holds, so the waiting-on-AI flag stays quiet then.
function isBusinessHoursNow(tz = 'America/New_York') {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: 'numeric', hour12: false }).formatToParts(new Date());
    const wd = parts.find(p => p.type === 'weekday')?.value;
    let h = parseInt(parts.find(p => p.type === 'hour')?.value, 10);
    if (h === 24) h = 0;
    if (wd === 'Sat' || wd === 'Sun') return false;
    return h >= BUSINESS_START_HOUR && h < BUSINESS_END_HOUR;
  } catch {
    const d = new Date();
    if (d.getDay() === 0 || d.getDay() === 6) return false;
    return d.getHours() >= BUSINESS_START_HOUR && d.getHours() < BUSINESS_END_HOUR;
  }
}

function formatApptTime(iso, tz = 'America/New_York') {
  const d = asDate(iso);
  if (!d) return '';
  const now = new Date();
  const tmr = new Date(now.getTime() + 86400000);
  const t = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(d);
  if (dayKeyInTz(d, tz) === dayKeyInTz(now, tz)) return `Today ${t}`;
  if (dayKeyInTz(d, tz) === dayKeyInTz(tmr, tz)) return `Tomorrow ${t}`;
  const wd = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(d);
  return `${wd} ${t}`;
}

// ---- Snooze ----------------------------------------------------------------
// A rep snoozing a lead in LeadDetail writes leads.snoozed_until and nothing else:
// NO edge function, cron, or follow-up job reads that column, so the AI keeps
// working the lead exactly as before. Snooze is a VIEW control — "stop putting this
// in front of me until the date" — and this is the only place that honours it.
// An expired snoozed_until is simply ignored, so the lead flows back into its normal
// section on the next load with no cleanup job required.
const isSnoozed = (lead) => !!lead.snoozed_until && asMs(lead.snoozed_until) > Date.now();
// A snooze does not expire: LeadDetail writes a date that never arrives (SNOOZE_FOREVER
// there — keep the two in step). Anything more than a year out is a forever-snooze and
// reads "until you wake them" rather than a countdown. Legacy dated snoozes (the old
// 1–30 day picker) fall under the threshold and still show their real wake date, which
// is right: that is what the rep was promised when they set it.
const SNOOZE_INDEFINITE_AFTER_MS = 365 * 24 * 60 * 60 * 1000;
const isSnoozedForever = (lead) => asMs(lead.snoozed_until) - Date.now() > SNOOZE_INDEFINITE_AFTER_MS;
// Whole days until wake, rounded up — 0 means "later today".
const daysUntil = (iso) => Math.max(0, Math.ceil((asMs(iso) - Date.now()) / 86400000));
// Wake time in the TENANT's zone. Near dates read as Today/Tomorrow/weekday; anything
// a week or more out gets a real calendar date, because "Tue" is meaningless at 30 days.
function formatWakeTime(iso, tz = 'America/New_York') {
  const d = asDate(iso);
  if (!d) return '';
  const now = new Date();
  const t = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(d);
  if (dayKeyInTz(d, tz) === dayKeyInTz(now, tz)) return `Today ${t}`;
  if (dayKeyInTz(d, tz) === dayKeyInTz(new Date(now.getTime() + 86400000), tz)) return `Tomorrow ${t}`;
  if (d.getTime() - now.getTime() < 7 * 86400000) {
    return `${new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(d)} ${t}`;
  }
  return new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(d);
}

// Raw attention_reason keys from score-lead → human phrases. Anything unmapped
// falls back to de-underscored Title Case so a raw enum never reaches a rep.
const REASON_LABELS = {
  requested_callback: 'Asked for a callback',
  agreed_to_meeting: 'Agreed to a meeting',
  pricing_inquiry: 'Asked about pricing',
  buying_signal: 'Showed a buying signal',
  timeline_urgent: 'Has an urgent timeline',
  explicit_timeline: 'Shared a timeline',
  high_interest_question: 'Lots of questions, high interest',
  high_ai_critical_score: 'High-intent conversation',
  ai_critical_score: 'High-intent conversation',
  has_property_offer: 'Has a property to offer',
};
const humanizeReason = (r) =>
  r ? (REASON_LABELS[r] || String(r).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())) : null;

// Leads carry a hot_reason from the decision core — the actual reason the AI handed
// off, far richer than the keyword-derived attention_reasons. It drives both the label
// text and the hot / warm / booked tag. Grouping mirrors sync_sales_metrics.
const HOT_REASON_LABELS = {
  appointment_booked: 'Booked a meeting',
  appointment_booked_web: 'Booked a meeting',
  booking_stalled: 'Stalled mid-booking',
  callback_request: 'Requested a callback',
  info_request: 'Info request',
  info_request_fulfilled: 'Info request',
  referral_contact_handoff: 'Referral given',
  role_mismatch_engaged_handoff: 'Engaged, not a role fit',
  scheduling_no_slots_handoff: 'Wants to book, no slot fit',
  voice_call_disposition: 'Voice call outcome',
  // Voice AI v1 Phase 9: a live in-call transfer was attempted but not answered
  // (retell-call-lifecycle's notifyFailedLiveTransfer). Without this entry the
  // fallback below would still render a readable "Voice Live Transfer Failed" label
  // (humanizeReason title-cases the raw value) — added anyway for the same clear,
  // hand-written phrasing every other reason here gets.
  voice_live_transfer_failed: 'Live transfer not answered',
};
// Handoffs WITHOUT a booking. These are hot leads like any other (status 'Hot Lead' —
// there is no warm tier, see supabase/functions/_shared/handoff.ts); the split here is
// purely so a row reads "Booked" vs "Handoff" vs "Hot" instead of three identical tags.
const UNBOOKED_HANDOFF_REASONS = new Set([
  'callback_request', 'booking_stalled', 'info_request', 'info_request_fulfilled',
  'referral_contact_handoff', 'role_mismatch_engaged_handoff', 'scheduling_no_slots_handoff',
  'voice_live_transfer_failed',
]);
const BOOKED_REASONS = new Set(['appointment_booked', 'appointment_booked_web']);

// The single most important reason this lead needs you. hot_reason first (specific),
// then the humanized attention_reason, then a generic fallback. Every row also gets a
// hot / warm / booked tag so no two read identically.
function buildWhy(lead, msgs, appt, hotThreshold, tz = 'America/New_York') {
  const ar = Array.isArray(lead.attention_reasons) ? lead.attention_reasons.filter(Boolean) : [];
  const hr = lead.hot_reason || null;
  const reason = (hr && HOT_REASON_LABELS[hr]) || humanizeReason(ar[0]) || (hr ? humanizeReason(hr) : null);
  const waitedRel = formatRelativeTime(msgs.lastInboundAt);
  const waiting = msgs.lastInboundAt && (!msgs.lastOutboundAt || asMs(msgs.lastInboundAt) > asMs(msgs.lastOutboundAt));
  const isHot = lead.status === 'Hot Lead' || lead.hot_score >= hotThreshold;

  // One category tag per row.
  let handoff;
  if (appt || lead.booking_completed || BOOKED_REASONS.has(hr)) handoff = 'booked';
  else if (UNBOOKED_HANDOFF_REASONS.has(hr)) handoff = 'handoff';
  else if (isHot) handoff = 'hot';
  else if (lead.handoff_triggered) handoff = 'handoff';
  else handoff = null;

  if (appt) return { key: 'meeting', handoff, text: `Meeting ${formatApptTime(appt.scheduled_time, tz)}`, act: 'Prep & confirm', waited: null };
  if (lead.booking_completed) return { key: 'meeting', handoff, text: reason || 'Booking completed, confirm details', act: 'Confirm', waited: null };
  if (lead.handoff_triggered) return { key: 'hot', handoff, text: reason || 'Handed off, ready for you', act: 'Take over', waited: waitedRel };
  if (isHot) return { key: 'hot', handoff, text: reason || `Hot lead, score ${lead.hot_score}`, act: 'Reply now', waited: waitedRel };
  if (waiting) return { key: 'question', handoff, text: reason || `Replied ${waitedRel} ago, waiting on your reply`, act: 'Reply', waited: waitedRel };
  if (reason) return { key: 'intent', handoff, text: reason, act: 'Open', waited: waitedRel };
  return { key: 'intent', handoff, text: 'Needs a look', act: 'Open', waited: waitedRel };
}

// ============================================================================
// PRESENTATION PRIMITIVES
// ============================================================================
const Avatar = ({ name, hot, size = 36 }) => (
  <div style={{ width: size, height: size }}
    className={`shrink-0 rounded-full grid place-items-center text-[12px] font-bold ${hot ? 'bg-orange-100 text-orange-700' : 'bg-slate-200 text-slate-600'}`}>
    {initials(name)}
  </div>
);

const ReasonTile = ({ rkey, size = 'lg' }) => {
  const r = REASON[rkey] || REASON.intent; const { Icon } = r;
  const dim = size === 'lg' ? 'w-11 h-11' : size === 'md' ? 'w-10 h-10' : 'w-8 h-8';
  return <span className={`grid place-items-center ${dim} rounded-xl shrink-0 ${r.cls}`}><Icon className="w-[19px] h-[19px]" /></span>;
};

const LeadsTabs = ({ view, setView, attnCount, benchCount, showBench }) => (
  <div className="inline-flex p-0.5 rounded-xl bg-slate-100">
    <button onClick={() => setView('attention')}
      className={`px-4 h-9 rounded-lg text-[13px] font-semibold inline-flex items-center gap-1.5 transition-colors ${view === 'attention' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
      Needs you <span className={view === 'attention' ? 'text-[11px] font-bold text-white bg-slate-900 rounded-full px-1.5' : 'text-slate-400'}>{attnCount}</span>
    </button>
    {showBench && (
      <button onClick={() => setView('bench')}
        className={`px-4 h-9 rounded-lg text-[13px] font-semibold inline-flex items-center gap-1.5 transition-colors ${view === 'bench' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
        <Users className="w-[15px] h-[15px]" />Keep warm <span className={view === 'bench' ? 'text-[11px] font-bold text-white bg-slate-900 rounded-full px-1.5' : 'text-slate-400'}>{benchCount}</span>
      </button>
    )}
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function DashboardV2() {
  const { user } = useAuth();

  const [leads, setLeads] = useState([]);
  const [messagesByLead, setMessagesByLead] = useState({});
  const [appointmentsByLead, setAppointmentsByLead] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [showAddLead, setShowAddLead] = useState(false);
  const [addLeadTab, setAddLeadTab] = useState('single');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('attention'); // 'attention' | 'bench'
  const [openSections, setOpenSections] = useState({ now: true, active: true, quiet: false, snoozed: false });
  const [hotThreshold, setHotThreshold] = useState(80);
  const [tenantIndustry, setTenantIndustry] = useState(null);
  const [tenantTz, setTenantTz] = useState('America/New_York');

  // Interject keyword: flag Active chats whose conversation mentions a term the
  // tenant cares about (competitor, "attorney", "cash", etc.). Persisted per-browser.
  // The match runs OFF the render critical path (see effect below) so it never
  // delays the dashboard — rows just light up a beat later.
  const [interjectKw, setInterjectKw] = useState(() => {
    try { return localStorage.getItem('surfox_interject_kw') || ''; } catch { return ''; }
  });
  const [debouncedKw, setDebouncedKw] = useState(interjectKw);
  const [keywordHits, setKeywordHits] = useState(() => new Set());

  const isGlobalAdmin = useMemo(
    () => user?.role === 'global_admin' || user?.user_metadata?.role === 'global_admin',
    [user]
  );
  const [allTenants, setAllTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('all');
  const [loadingTenants, setLoadingTenants] = useState(false);

  const effectiveTenantId = useMemo(() => {
    if (isGlobalAdmin && selectedTenantId !== 'all') return selectedTenantId;
    return user?.tenant_id || null;
  }, [isGlobalAdmin, selectedTenantId, user?.tenant_id]);

  // Deep link from the Go-Live companion: ?add=leads opens Add Lead on Bulk import.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('add') === 'leads') {
      setAddLeadTab('bulk');
      setShowAddLead(true);
      searchParams.delete('add');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ---- DATA FETCH (preserved) ------------------------------------------------
  const fetchLeads = useCallback(async (isRefresh = false) => {
    if (!user) { setLoading(false); return; }
    if (isRefresh) setRefreshing(true);
    else { setLoading(true); setLeads([]); setMessagesByLead({}); setAppointmentsByLead({}); }
    setError('');

    try {
      const SLIM_LEAD_FIELDS = 'id,name,first_name,last_name,phone,phone_digits,email,notes,summary,status,qualification_mismatch,mismatch_details,bench_reason,mismatch_detected_at,fit_for,bench_location,tenant_id,campaign_id,handoff_triggered,ai_conversation_enabled,disposition_reason,sms_opted_out,booking_completed,marked_hot_at,hot_reason,pinned_to_dashboard,snoozed_until,last_message_at,requested_followup_date,created_at,assigned_to_sales_team_id,session_id,campaigns!campaign_id(name,talk_track_specialty),lead_scores(hot_score,requires_immediate_attention,alert_priority,alert_triggers,attention_reasons)';

      const role = user?.role || user?.user_metadata?.role;
      const isTenantAdmin = TENANT_ADMIN_ROLES.includes(role);

      const buildLeadsQuery = async () => {
        let q = supabase
          .from('leads')
          .select(SLIM_LEAD_FIELDS)
          .or('status.not.in.("Cold Lead","Cold"),pinned_to_dashboard.eq.true')
          .eq('sms_opted_out', false)
          .order('created_at', { ascending: false })
          .order('created_at', { referencedTable: 'lead_scores', ascending: false })
          .limit(1, { referencedTable: 'lead_scores' });

        if (isGlobalAdmin) {
          if (selectedTenantId !== 'all') q = q.eq('tenant_id', selectedTenantId);
        } else if (isTenantAdmin) {
          if (!user.tenant_id) throw new Error('Tenant admin missing tenant information');
          q = q.eq('tenant_id', user.tenant_id);
        } else {
          if (!user.tenant_id) throw new Error('User missing tenant information');
          const { data: profile, error: pErr } = await supabase
            .from('sales_team').select('id').eq('user_profile_id', user.id).single();
          if (pErr || !profile) throw new Error('User not assigned to sales team');
          q = q.eq('tenant_id', user.tenant_id).eq('assigned_to_sales_team_id', profile.id);
        }
        return q;
      };

      const nowIso = new Date().toISOString();
      // Per-lead message stats are aggregated SERVER-SIDE (get_lead_message_stats).
      // This used to pull up to 10,000 raw message rows per tenant and count them in
      // the browser — which silently truncated once a tenant crossed that many
      // messages: the dropped rows made their leads look like they had 0 inbound, so
      // they failed the "outbound>=2 && inbound>=1" gate and VANISHED from the board.
      // The RPC counts every row and returns one small row per lead instead.
      const fetchMsgs = (tid) => supabase.rpc('get_lead_message_stats', { p_tenant_id: tid });
      const fetchAppts = (tid) => supabase
        .from('appointments').select('lead_id, scheduled_time, status')
        .eq('tenant_id', tid).eq('status', 'scheduled').gte('scheduled_time', nowIso)
        .order('scheduled_time', { ascending: true });

      const isAll = isGlobalAdmin && selectedTenantId === 'all';
      let leadsResult, msgResults, apptResults, tenantIds;

      if (!isAll && effectiveTenantId) {
        tenantIds = [effectiveTenantId];
        const [lr, mr, ar] = await Promise.all([buildLeadsQuery(), fetchMsgs(effectiveTenantId), fetchAppts(effectiveTenantId)]);
        leadsResult = lr; msgResults = [mr]; apptResults = [ar];
      } else {
        leadsResult = await buildLeadsQuery();
        if (leadsResult.error) throw new Error('Failed to load leads: ' + leadsResult.error.message);
        tenantIds = [...new Set((leadsResult.data || []).map(l => l.tenant_id).filter(Boolean))];
        if (tenantIds.length) {
          [msgResults, apptResults] = await Promise.all([
            Promise.all(tenantIds.map(fetchMsgs)),
            Promise.all(tenantIds.map(fetchAppts)),
          ]);
        } else { msgResults = []; apptResults = []; }
      }
      if (leadsResult.error) throw new Error('Failed to load leads: ' + leadsResult.error.message);

      const withScores = (leadsResult.data || []).map(lead => {
        const ls = (Array.isArray(lead.lead_scores) ? lead.lead_scores[0] : lead.lead_scores) || {};
        const camp = lead.campaigns || {};
        return {
          ...lead,
          campaign: camp.name || 'No Campaign',
          specialty: camp.talk_track_specialty || null,
          hot_score: ls.hot_score || 0,
          requires_immediate_attention: ls.requires_immediate_attention || false,
          alert_priority: ls.alert_priority || 'none',
          alert_triggers: ls.alert_triggers || {},
          attention_reasons: ls.attention_reasons || [],
        };
      });
      setLeads(withScores);

      const apptMap = {};
      apptResults.flatMap(r => r.data || []).forEach(a => { if (!apptMap[a.lead_id]) apptMap[a.lead_id] = a; });

      // One pre-aggregated row per lead from the RPC — no client-side counting.
      const msgMap = {};
      msgResults.flatMap(r => r.data || []).forEach(s => {
        msgMap[s.lead_id] = {
          lastMessageAt: s.last_message_at,
          inboundCount: s.inbound_count || 0,
          outboundCount: s.outbound_count || 0,
          lastInboundAt: s.last_inbound_at,
          lastOutboundAt: s.last_outbound_at,
          lastOutboundSender: s.last_outbound_sender,
        };
      });

      setAppointmentsByLead(apptMap);
      setMessagesByLead(msgMap);
    } catch (err) {
      console.error('[DashboardV2] fetchLeads:', err);
      setError(err.message);
    } finally { setLoading(false); setRefreshing(false); }
  }, [user, isGlobalAdmin, selectedTenantId, effectiveTenantId]);

  // ---- EFFECTS ---------------------------------------------------------------
  useEffect(() => { document.title = 'Leads – SurFox'; }, []);

  useEffect(() => {
    if (!isGlobalAdmin) return;
    setLoadingTenants(true);
    supabase.from('tenants').select('id, name').or(VISIBLE_TENANTS_OR).order('name').then(({ data, error }) => {
      if (!error && data) setAllTenants(data);
      setLoadingTenants(false);
    });
  }, [isGlobalAdmin]);

  useEffect(() => {
    if (!effectiveTenantId) { setTenantIndustry(null); return; }
    supabase.from('tenants').select('industry').eq('id', effectiveTenantId).maybeSingle()
      .then(({ data }) => setTenantIndustry(data?.industry || null));
  }, [effectiveTenantId]);

  // Tenant timezone from platform_settings — drives every time-of-day calc below.
  useEffect(() => {
    if (!effectiveTenantId) { setTenantTz('America/New_York'); return; }
    supabase.from('platform_settings').select('value').eq('key', 'timezone').eq('tenant_id', effectiveTenantId).maybeSingle()
      .then(({ data }) => {
        const raw = data?.value;
        const val = typeof raw === 'string' ? raw : (raw && typeof raw === 'object' ? (raw.value || raw.timezone) : raw);
        setTenantTz(toIANA(val));
      });
  }, [effectiveTenantId]);

  useEffect(() => {
    if (!(user?.tenant_id || isGlobalAdmin)) return;
    const fetchSettings = async () => {
      try {
        let q = supabase.from('platform_settings').select('value').eq('key', 'ai_min_escalation_score').limit(1);
        if (!isGlobalAdmin && user.tenant_id) q = q.eq('tenant_id', user.tenant_id);
        const { data, error } = await q.maybeSingle();
        if (!error) {
          const raw = data?.value;
          const num = typeof raw === 'object' ? Number(raw) : Number(String(raw));
          if (!Number.isNaN(num) && num > 0) setHotThreshold(num);
        }
      } catch { /* keep default */ }
    };
    (async () => { await Promise.all([fetchSettings(), fetchLeads()]); })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tenant_id, isGlobalAdmin, selectedTenantId]);

  // ---- BENCH ("Keep warm") ---------------------------------------------------
  // Industry-agnostic: any engaged lead the AI flagged as not-a-fit
  // (qualification_mismatch / bench_reason) lands here, regardless of industry,
  // so these never leak into the attention sections unnoticed. Grouping differs:
  // Staffing groups by specialty (as before); everyone else groups by campaign.
  // The tab itself appears whenever the bench is non-empty (computed below).
  const benchGroupMode = useMemo(() => {
    if (isGlobalAdmin && selectedTenantId === 'all') return 'campaign';
    return tenantIndustry === 'Staffing' ? 'specialty' : 'campaign';
  }, [isGlobalAdmin, selectedTenantId, tenantIndustry]);

  // ---- SECTION PARTITION (preserved logic) -----------------------------------
  const sections = useMemo(() => {
    const cutoff = Date.now() - ACTIVITY_WINDOW_DAYS * 86400000;
    const lower = search.trim().toLowerCase();
    const phoneQ = search.replace(/\D/g, '');
    const needs = [], active = [], quiet = [], bench = [], snoozed = [];

    for (const lead of leads) {
      if (lower) {
        const textMatch = [lead.name, lead.first_name, lead.last_name, lead.email, lead.notes, lead.summary, lead.campaign, lead.status, lead.mismatch_details, lead.bench_location, ...(Array.isArray(lead.fit_for) ? lead.fit_for : [])]
          .some(v => v && v.toString().toLowerCase().includes(lower));
        const phoneMatch = phoneQ.length >= 3 && lead.phone_digits && lead.phone_digits.includes(phoneQ);
        if (!textMatch && !phoneMatch) continue;
      }
      if (TERMINAL_STATUSES.includes(lead.status)) continue;

      const msgs = messagesByLead[lead.id] || { inboundCount: 0, outboundCount: 0, lastMessageAt: null, lastInboundAt: null, lastOutboundAt: null };
      const appt = appointmentsByLead[lead.id] || null;

      // Snooze is checked FIRST, ahead of the bench and ahead of the two-way-conversation
      // gate below: a rep explicitly said "not now", so the lead belongs in exactly one
      // place — Snoozed — no matter what else is true about it. A snoozed bench lead
      // therefore leaves the Keep-warm tab until it wakes, by design.
      if (isSnoozed(lead)) { snoozed.push({ lead, msgs, appt }); continue; }

      if (lead.qualification_mismatch === true || !!lead.bench_reason) { bench.push({ lead, msgs, appt }); continue; }
      if (!(msgs.outboundCount >= 2 && msgs.inboundCount >= 1)) continue;

      const inNeeds = lead.status === 'Hot Lead' || lead.handoff_triggered === true || lead.booking_completed === true || appt !== null || lead.pinned_to_dashboard === true;
      if (inNeeds) { needs.push({ lead, msgs, appt }); continue; }

      // A lead SurFox closed for a RECORDED reason (disqualified, opted out, a rep
      // switched the AI off) is finished — not a conversation the AI is handling, and
      // not something a rep needs to see. Drop it so closed leads never fill the board.
      // AI-off leads with NO recorded reason deliberately stay: they may have been
      // switched off by mistake, and ActiveRow flags the still-engaged ones amber.
      if (lead.ai_conversation_enabled === false && (!!lead.disposition_reason || lead.sms_opted_out === true)) continue;

      const lastTs = msgs.lastMessageAt || lead.last_message_at || null;
      const lastMs = lastTs ? asMs(lastTs) : null;
      if (lastMs && lastMs >= cutoff) active.push({ lead, msgs, appt });
      else quiet.push({ lead, msgs, appt });
    }

    // Sorts (preserved intent)
    needs.sort((a, b) => {
      const ap = a.lead.pinned_to_dashboard ? 1 : 0, bp = b.lead.pinned_to_dashboard ? 1 : 0; if (ap !== bp) return bp - ap;
      const apr = a.lead.requires_immediate_attention ? 1 : 0, bpr = b.lead.requires_immediate_attention ? 1 : 0; if (apr !== bpr) return bpr - apr;
      const aHas = !!a.appt, bHas = !!b.appt; if (aHas !== bHas) return aHas ? -1 : 1;
      if (aHas) return asMs(a.appt.scheduled_time) - asMs(b.appt.scheduled_time);
      return (b.lead.marked_hot_at ? asMs(b.lead.marked_hot_at) : 0) - (a.lead.marked_hot_at ? asMs(a.lead.marked_hot_at) : 0);
    });
    active.sort((a, b) => (b.msgs.lastInboundAt ? asMs(b.msgs.lastInboundAt) : 0) - (a.msgs.lastInboundAt ? asMs(a.msgs.lastInboundAt) : 0));
    const byLast = (a, b) => {
      const al = a.msgs.lastMessageAt || a.lead.last_message_at, bl = b.msgs.lastMessageAt || b.lead.last_message_at;
      if (!al && !bl) return 0; if (!al) return 1; if (!bl) return -1;
      return asMs(bl) - asMs(al);
    };
    quiet.sort(byLast); bench.sort(byLast);
    // Soonest to wake first — the top of the list is what comes back to you next.
    snoozed.sort((a, b) => asMs(a.lead.snoozed_until) - asMs(b.lead.snoozed_until));

    return { needs, active, quiet, bench, snoozed };
  }, [leads, messagesByLead, appointmentsByLead, search, hotThreshold]);

  // Quiet visible = within the archive window (older are archived by the cron / hidden here)
  const quietVisible = useMemo(
    () => sections.quiet.filter(e => daysSince(e.msgs.lastMessageAt || e.lead.last_message_at) < QUIET_ARCHIVE_DAYS),
    [sections.quiet]
  );

  // Persist + debounce the interject keyword so we don't refetch on every keystroke.
  useEffect(() => {
    try { localStorage.setItem('surfox_interject_kw', interjectKw); } catch { /* ignore */ }
    const t = setTimeout(() => setDebouncedKw(interjectKw.trim()), 300);
    return () => clearTimeout(t);
  }, [interjectKw]);

  // Stable dependency: only the set of Active lead ids, joined, so the background
  // fetch refires when that set actually changes (not on every array re-identity).
  const activeLeadKey = useMemo(() => sections.active.map(e => e.lead.id).join(','), [sections.active]);

  // Active leads whose AI is off with NO reason on the row. These are the ambiguous
  // ones (wrong-number kill / quality-monitor kill / pre-fix manual toggle) where we
  // need the last classified intent to know if stopping was expected.
  const unexplainedOffKey = useMemo(() => sections.active
    .filter(e => e.lead.ai_conversation_enabled === false && !e.lead.disposition_reason && !e.lead.sms_opted_out)
    .map(e => e.lead.id).join(','), [sections.active]);

  // ---- BACKGROUND: last classified intent for unexplained AI-off leads ---------
  // Off the critical path, scoped to a handful of leads, and only fetched when there
  // are any. Rows resolve from "AI off" to a specific reason a beat later.
  const [lastIntentByLead, setLastIntentByLead] = useState({});
  useEffect(() => {
    const ids = unexplainedOffKey ? unexplainedOffKey.split(',') : [];
    if (!ids.length) { setLastIntentByLead({}); return; }

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('intent_classifications')
        .select('lead_id, intent, created_at')
        .in('lead_id', ids)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) { console.error('[DashboardV2] intent lookup failed:', error); return; }
      const map = {};
      (data || []).forEach(r => { if (!map[r.lead_id]) map[r.lead_id] = r.intent; }); // first = newest
      setLastIntentByLead(map);
    })();
    return () => { cancelled = true; };
  }, [unexplainedOffKey]);

  // ---- BACKGROUND: conversation keyword match (off the critical path) ---------
  // Runs ONLY when a keyword is set and ONLY over Active leads (dozens, not the
  // full 10k-message pull). The match is done server-side via ILIKE, so message
  // bodies never reach the browser — we get back just the matching lead ids.
  useEffect(() => {
    const kw = debouncedKw;
    const ids = activeLeadKey ? activeLeadKey.split(',') : [];
    if (!kw || !ids.length) { setKeywordHits(new Set()); return; }

    let cancelled = false; // stale-guard for refresh / tenant switch / keyword change
    (async () => {
      // Escape LIKE wildcards so a literal % or _ doesn't turn into a wildcard match.
      const safe = kw.replace(/[%_]/g, (m) => `\\${m}`);
      let q = supabase.from('messages').select('lead_id').in('lead_id', ids).ilike('message_body', `%${safe}%`);
      if (effectiveTenantId) q = q.eq('tenant_id', effectiveTenantId);
      const { data, error } = await q;
      if (cancelled) return;
      if (error) { console.error('[DashboardV2] keyword match failed:', error); return; }
      setKeywordHits(new Set((data || []).map(m => m.lead_id)));
    })();
    return () => { cancelled = true; };
  }, [debouncedKw, activeLeadKey, effectiveTenantId]);

  const attnCount = sections.needs.length + sections.active.length + quietVisible.length;

  // The "Keep warm" tab shows whenever there's anyone on the bench (any industry).
  const showBenchTab = sections.bench.length > 0;

  // Attention summary lenses (derived from sections, not analytics)
  const lenses = useMemo(() => {
    let waiting = 0, meetings = 0, heating = 0;
    sections.needs.forEach(({ lead, msgs, appt }) => {
      const w = buildWhy(lead, msgs, appt, hotThreshold);
      if (appt) meetings++;
      else if (w.key === 'question' || w.key === 'unanswered') waiting++;
      else if (w.key === 'hot' || w.key === 'intent') heating++;
    });
    return [
      { key: 'question', n: waiting, label: 'Waiting on you' },
      { key: 'meeting', n: meetings, label: 'Meetings' },
      { key: 'hot', n: heating, label: 'Heating up' },
      { key: 'cooling', n: quietVisible.length, label: 'Going quiet' },
    ];
  }, [sections.needs, quietVisible.length, hotThreshold]);

  // Today's agenda (real appointments scheduled today)
  const agenda = useMemo(() => {
    const todayKey = dayKeyInTz(new Date(), tenantTz);
    const out = [];
    Object.entries(appointmentsByLead).forEach(([leadId, a]) => {
      if (dayKeyInTz(asDate(a.scheduled_time), tenantTz) !== todayKey) return;
      const lead = leads.find(l => l.id === leadId);
      out.push({ time: a.scheduled_time, name: lead?.name || 'Lead', co: lead?.campaign });
    });
    return out.sort((x, y) => asMs(x.time) - asMs(y.time));
  }, [appointmentsByLead, leads, tenantTz]);

  // "Just happened" — recent inbound replies (real)
  const activity = useMemo(() => {
    const items = [];
    leads.forEach(l => { const m = messagesByLead[l.id]; if (m?.lastInboundAt) items.push({ name: l.name, at: m.lastInboundAt }); });
    return items.sort((a, b) => asMs(b.at) - asMs(a.at)).slice(0, 5);
  }, [leads, messagesByLead]);

  // ---- HANDLERS --------------------------------------------------------------
  // Open the lead in a new browser tab so the dashboard stays put behind it.
  const openLead = useCallback((id) => window.open(`/lead/${id}`, '_blank', 'noopener'), []);

  // NOTE: there is deliberately no manual wake action yet. A snooze is released only
  // when the lead texts back (handleincomingmessage / handle-web-chat-message clear
  // snoozed_until on any inbound). A rep-facing wake control is planned separately;
  // until it exists, snoozing is one-way from the UI.

  // Bench grouped by specialty (Staffing) or by campaign (everyone else).
  const benchGroups = useMemo(() => {
    const map = {};
    sections.bench.forEach(({ lead, msgs }) => {
      const [key, label] = benchGroupMode === 'specialty'
        ? [lead.specialty || 'other', specialtyLabel(lead.specialty)]
        : [lead.campaign || 'No Campaign', lead.campaign || 'No Campaign'];
      (map[key] ||= { label, people: [] }).people.push({ lead, msgs });
    });
    return Object.entries(map)
      .map(([key, g]) => ({ key, label: g.label, people: g.people }))
      .sort((a, b) => b.people.length - a.people.length);
  }, [sections.bench, benchGroupMode]);

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 flex items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">Leads</h1>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 ring-1 ring-inset ring-emerald-600/15">
                <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
                <span className="text-[11px] font-semibold text-emerald-700">Live</span>
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-0.5 hidden sm:block">The leads that need you right now — and exactly why.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => fetchLeads(true)} disabled={refreshing}
              className="w-10 h-10 grid place-items-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors">
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={() => { setAddLeadTab('single'); setShowAddLead(true); }} data-tour="add-lead-button"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors shadow-sm">
              <Plus className="w-4 h-4" /><span className="hidden sm:inline">Add lead</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Global admin tenant selector (preserved) */}
        {isGlobalAdmin && (
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-4 text-white flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl"><Globe className="w-5 h-5" /></div>
              <div>
                <h3 className="font-semibold">Platform Admin View</h3>
                <p className="text-sm text-white/80">{selectedTenantId === 'all' ? 'Viewing all tenants' : `Viewing: ${allTenants.find(t => t.id === selectedTenantId)?.name || selectedTenantId}`}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-white/80">Tenant:</label>
              <select value={selectedTenantId} onChange={(e) => setSelectedTenantId(e.target.value)} disabled={loadingTenants}
                className="border-0 rounded-lg px-3 py-2 text-sm bg-white/20 text-white focus:ring-2 focus:ring-white/50 focus:outline-none min-w-[200px] cursor-pointer disabled:opacity-50">
                <option value="all" className="text-slate-900 bg-white">All Tenants</option>
                {allTenants.map(t => <option key={t.id} value={t.id} className="text-slate-900 bg-white">{t.name || `Tenant ${t.id}`}</option>)}
              </select>
              {loadingTenants && <Loader2 className="w-4 h-4 text-white/70 animate-spin" />}
            </div>
          </div>
        )}

        {/* Switch + search */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <LeadsTabs view={view} setView={setView} attnCount={attnCount} benchCount={sections.bench.length} showBench={showBenchTab} />
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={view === 'bench' ? 'Search by name, location, or what they want…' : 'Search leads…'}
              className="w-full h-10 pl-11 pr-4 bg-white border border-slate-200 rounded-xl text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-all" />
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 animate-pulse">
                <div className="w-11 h-11 rounded-xl bg-slate-100" />
                <div className="flex-1 space-y-2"><div className="h-4 bg-slate-200 rounded w-1/3" /><div className="h-3 bg-slate-100 rounded w-1/2" /></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl border border-slate-200 text-center py-16 flex flex-col items-center">
            <div className="w-12 h-12 bg-red-100 rounded-full grid place-items-center mb-3"><AlertCircle className="w-6 h-6 text-red-500" /></div>
            <p className="text-slate-700 font-medium mb-1">Failed to load leads</p>
            <p className="text-sm text-slate-500 mb-4">{error}</p>
            <button onClick={() => { setError(''); fetchLeads(); }} className="px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-medium hover:bg-slate-800">Try again</button>
          </div>
        ) : (view === 'bench' && showBenchTab) ? (
          <BenchView groups={benchGroups} total={sections.bench.length} onOpen={openLead} />
        ) : (
          <AttentionView
            sections={{ ...sections, quiet: quietVisible }} lenses={lenses} agenda={agenda} activity={activity}
            openSections={openSections} setOpenSections={setOpenSections} hotThreshold={hotThreshold} onOpen={openLead}
            interjectKw={interjectKw} setInterjectKw={setInterjectKw} keywordHits={keywordHits} debouncedKw={debouncedKw}
            lastIntentByLead={lastIntentByLead} tz={tenantTz}
          />
        )}
      </div>

      {/* Add lead modal */}
      {showAddLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddLead(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-slate-900">Add New Lead</h3>
              <button onClick={() => setShowAddLead(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6"><AddLead initialTab={addLeadTab} onSuccess={() => { fetchLeads(); setShowAddLead(false); }} onCancel={() => setShowAddLead(false)} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ATTENTION VIEW
// ============================================================================
function AttentionView({ sections, lenses, agenda, activity, openSections, setOpenSections, hotThreshold, onOpen, interjectKw, setInterjectKw, keywordHits, debouncedKw, lastIntentByLead, tz }) {
  const SECTIONS = [
    { key: 'now', label: 'Needs you now', dot: 'bg-rose-500', note: 'sorted by urgency — work top down', data: sections.needs },
    { key: 'active', label: 'Active conversations', dot: 'bg-cyan-500', note: 'SurFox is handling these — here for awareness', data: sections.active },
    { key: 'quiet', label: 'Quiet', dot: 'bg-slate-300', note: `auto-archives after ${QUIET_ARCHIVE_DAYS} days of silence`, data: sections.quiet },
    // Snoozed sits last and closed: it is the one section that is deliberately NOT
    // asking for attention. It is hidden entirely when nobody is snoozed, so the
    // board doesn't grow an empty row for a feature most tenants never touch.
    ...(sections.snoozed.length ? [{ key: 'snoozed', label: 'Snoozed', dot: 'bg-violet-400', note: 'paused by you — SurFox stays quiet until the lead texts back', data: sections.snoozed }] : []),
  ];
  const empty = sections.needs.length + sections.active.length + sections.quiet.length + sections.snoozed.length === 0;

  return (
    <>
      {/* calm summary lenses */}
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        {lenses.map(l => { const r = REASON[l.key] || REASON.intent; const { Icon } = r; return (
          <div key={l.label} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3.5 py-3">
            <span className={`grid place-items-center w-9 h-9 rounded-lg shrink-0 ${r.cls}`}><Icon className="w-[17px] h-[17px]" /></span>
            <div className="min-w-0"><p className="text-[20px] font-bold text-slate-900 leading-none tabular-nums">{l.n}</p><p className="text-[11.5px] text-slate-500 truncate mt-0.5">{l.label}</p></div>
          </div>
        ); })}
      </div>

      <div className="mt-5 grid grid-cols-1 xl:grid-cols-[1fr_332px] gap-6 items-start">
        {/* sections */}
        <div className="space-y-3">
          {empty ? (
            <div className="bg-white rounded-2xl border border-slate-200 text-center py-16 flex flex-col items-center">
              <img src="/transparentfoxsitting.png" alt="SurFox" className="h-28 w-auto mb-3" />
              <p className="text-slate-700 font-medium">Nothing needs you right now. Nicely done.</p>
            </div>
          ) : SECTIONS.map(s => {
            const isOpen = openSections[s.key];
            return (
              <div key={s.key} className={`rounded-2xl ${isOpen ? '' : 'border border-slate-200 bg-white shadow-sm'}`}>
                <button onClick={() => setOpenSections(o => ({ ...o, [s.key]: !o[s.key] }))}
                  className={`w-full flex items-center gap-2.5 ${isOpen ? 'mb-3 px-1' : 'py-3 px-4'}`}>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                  <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                  <h3 className="text-[14px] font-bold text-slate-900">{s.label}</h3>
                  <span className={`grid place-items-center min-w-5 h-5 px-1.5 rounded-full text-[11.5px] font-bold tabular-nums ${isOpen ? 'bg-slate-100 text-slate-500' : 'bg-slate-900 text-white'}`}>{s.data.length}</span>
                  <span className="text-[12.5px] text-slate-400 truncate ml-1 text-left">· {s.note}</span>
                </button>
                {isOpen && (
                  <div className="space-y-2.5">
                    {s.key === 'active' && (
                      <div className="flex items-center gap-2 px-1 pb-0.5">
                        <span className="text-[11.5px] text-slate-400 shrink-0">Flag chats mentioning</span>
                        <div className="relative">
                          <input
                            value={interjectKw}
                            onChange={(e) => setInterjectKw(e.target.value)}
                            placeholder="e.g. cash, attorney, competitor"
                            className="h-8 w-56 pl-3 pr-7 bg-white border border-slate-200 rounded-lg text-[12.5px] placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 transition-all"
                          />
                          {interjectKw && (
                            <button type="button" onClick={() => setInterjectKw('')}
                              className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600">
                              <X className="w-[13px] h-[13px]" />
                            </button>
                          )}
                        </div>
                        {debouncedKw && (
                          <span className="text-[11.5px] text-slate-400 shrink-0">
                            {keywordHits.size > 0
                              ? `${keywordHits.size} chat${keywordHits.size === 1 ? '' : 's'} mention “${debouncedKw}”`
                              : `No active chats mention “${debouncedKw}”`}
                          </span>
                        )}
                      </div>
                    )}
                    {s.data.length === 0
                      ? <p className="text-[13px] text-slate-400 px-1 pb-2">Nothing here.</p>
                      : s.data.map(e => s.key === 'now'
                        ? <NeedsRow key={e.lead.id} entry={e} hotThreshold={hotThreshold} onOpen={onOpen} tz={tz} />
                        : s.key === 'active'
                          ? <ActiveRow key={e.lead.id} entry={e} onOpen={onOpen} highlighted={keywordHits.has(e.lead.id)} kw={interjectKw.trim()} lastIntent={lastIntentByLead[e.lead.id]} tz={tz} />
                          : s.key === 'snoozed'
                            ? <SnoozedRow key={e.lead.id} entry={e} onOpen={onOpen} tz={tz} />
                            : <QuietRow key={e.lead.id} entry={e} onOpen={onOpen} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* rail */}
        <div className="space-y-5">
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2"><span className="grid place-items-center w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600"><Calendar className="w-[15px] h-[15px]" /></span><h4 className="text-[14px] font-bold text-slate-900">Today's agenda</h4></div>
              <span className="text-[12px] font-medium text-slate-400">{agenda.length} call{agenda.length === 1 ? '' : 's'}</span>
            </div>
            <div className="px-5 py-4 space-y-3">
              {agenda.length === 0 ? <p className="text-[12.5px] text-slate-400">No calls scheduled today.</p> :
                agenda.map((a, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-14 text-right shrink-0"><span className="text-[14px] font-bold text-slate-900 leading-none">{new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(asDate(a.time))}</span></div>
                    <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                    <div className="min-w-0"><p className="text-[13px] font-semibold text-slate-800 truncate">{a.name}</p>{a.co && <p className="text-[11.5px] text-slate-500 truncate">{a.co}</p>}</div>
                  </div>
                ))}
            </div>
          </div>

          {activity.length > 0 && (
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-4"><Zap className="w-4 h-4 text-cyan-500" /><h4 className="text-[14px] font-bold text-slate-900">Just happened</h4></div>
              <div className="relative pl-4 space-y-3.5 before:absolute before:left-[5px] before:top-1.5 before:bottom-1.5 before:w-px before:bg-slate-100">
                {activity.map((a, i) => (
                  <div key={i} className="relative">
                    <span className="absolute -left-4 top-1 w-2.5 h-2.5 rounded-full ring-2 ring-white bg-cyan-500" />
                    <p className="text-[12.5px] text-slate-700 leading-snug"><span className="font-semibold text-slate-900">{a.name}</span> replied</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{formatRelativeTime(a.at)} ago</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// A lead created by the website chat widget carries session_id (handle-web-chat-message
// sets it; nothing else does). Badged so a rep knows before opening it that this person
// came from the website, not from a list we texted (2026-09-10). Needs session_id in
// SLIM_LEAD_FIELDS: a column missing from an explicit select reads undefined, not an error.
function WebChatBadge({ lead }) {
  if (!lead?.session_id) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-600/20 shrink-0" title="This lead started a conversation in the chat on your website">
      <Globe className="w-[10px] h-[10px]" />Web chat
    </span>
  );
}

// ---- attention rows ----------------------------------------------------------
function NeedsRow({ entry, hotThreshold, onOpen, tz }) {
  const { lead, msgs, appt } = entry;
  const why = buildWhy(lead, msgs, appt, hotThreshold, tz);
  return (
    <div onClick={() => onOpen(lead.id)} className="group flex items-center gap-4 rounded-xl bg-white border border-slate-200 px-4 py-3.5 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer">
      <ReasonTile rkey={why.key} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {lead.pinned_to_dashboard && <Pin className="w-[13px] h-[13px] text-amber-500 shrink-0" />}
          <span className="font-semibold text-slate-900 truncate">{lead.name || 'Unknown Lead'}</span>
          <WebChatBadge lead={lead} />
          {lead.campaign && lead.campaign !== 'No Campaign' && <span className="text-[12.5px] text-slate-400 truncate">· {lead.campaign}</span>}
          {why.handoff === 'booked'
            ? <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20"><Calendar className="w-[10px] h-[10px]" />Booked</span>
            : why.handoff === 'hot'
              ? <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20"><Flame className="w-[10px] h-[10px]" />Hot{lead.hot_score >= 50 ? ` · ${lead.hot_score}` : ''}</span>
              : why.handoff === 'handoff'
                ? <span className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20">Handoff</span>
                : lead.hot_score >= 85
                  ? <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-600/20"><Flame className="w-[10px] h-[10px]" />{lead.hot_score}</span>
                  : null}
        </div>
        <p className="mt-0.5 text-[13.5px] text-slate-700 leading-snug truncate font-medium" title={lead.summary || undefined}>{why.text}</p>
      </div>
      {why.waited && <span className="hidden xl:block text-[11.5px] text-slate-400 shrink-0 whitespace-nowrap">waiting {why.waited}</span>}
    </div>
  );
}

function ActiveRow({ entry, onOpen, highlighted, kw, lastIntent, tz }) {
  const { lead, msgs } = entry;
  const rel = formatRelativeTime(msgs.lastInboundAt || msgs.lastMessageAt);
  // AI-not-carrying-it signals (facts, not judgment):
  const unanswered = !!msgs.lastInboundAt && (!msgs.lastOutboundAt || asMs(msgs.lastInboundAt) > asMs(msgs.lastOutboundAt));
  const gapHours = msgs.lastInboundAt ? (Date.now() - asMs(msgs.lastInboundAt)) / 3600000 : 0;
  //  • awaitingReply — inbound unanswered for REPLY_STALE_HOURS while inside business
  //    hours. Normal latency, nights and weekends stay quiet (legit holds).
  //    Never fires when the AI is switched off for this lead: SurFox stopped on
  //    purpose (kill intent, closed case), so no reply is coming BY DESIGN and
  //    calling that "no answer yet" reads as a dropped ball.
  const aiOff = lead.ai_conversation_enabled === false;
  const awaitingReply = !aiOff && unanswered && gapHours >= REPLY_STALE_HOURS && isBusinessHoursNow(tz);

  // Why is the AI off? Prefer what the row records; fall back to the last classified
  // intent. An AI that went quiet while the lead was still engaged is the shape of an
  // ACCIDENTAL shutoff, so that one gets a warning tone instead of a calm grey.
  const offReason = aiOff
    ? (DISPOSITION_LABELS[lead.disposition_reason]
        || (lead.sms_opted_out ? DISPOSITION_LABELS.opted_out : null))
    : null;
  const offUnexplained = aiOff && !offReason;
  const offLooksExpected = offUnexplained && lastIntent && DISENGAGED_INTENTS.has(lastIntent);
  const offLooksAccidental = offUnexplained && !!lastIntent && !DISENGAGED_INTENTS.has(lastIntent);
  //  • manualLast — newest message is a human/manual outbound, so a rep is already in
  //    this thread. Only when the thread is NOT sitting on an unanswered inbound.
  const manualLast = !unanswered && (msgs.lastOutboundSender === 'human' || msgs.lastOutboundSender === 'Manual');
  return (
    <div onClick={() => onOpen(lead.id)} className={`group flex items-center gap-4 rounded-xl px-4 py-3 transition-all cursor-pointer ${highlighted ? 'bg-amber-50/50 border border-amber-300 ring-1 ring-amber-200 hover:border-amber-400' : 'bg-white border border-slate-200 hover:border-slate-300'}`}>
      <ReasonTile rkey="question" size="md" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 truncate">{lead.name || 'Unknown Lead'}</span>
          <WebChatBadge lead={lead} />
          {lead.campaign && lead.campaign !== 'No Campaign' && <span className="text-[12.5px] text-slate-400 truncate">· {lead.campaign}</span>}
          {awaitingReply
            ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-rose-50 text-rose-600 text-[10.5px] font-semibold ring-1 ring-inset ring-rose-600/15"><Bell className="w-[10px] h-[10px]" />Lead replied, no answer yet</span>
            : manualLast
              ? <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-600 text-[10.5px] font-semibold ring-1 ring-inset ring-indigo-600/15" title="The most recent message was sent manually by a rep, not the AI">Manual reply sent</span>
              : offLooksAccidental
                ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[10.5px] font-semibold ring-1 ring-inset ring-amber-600/20" title={`AI is off, but this lead's last intent was ${lastIntent}, not a disengagement. It may have been switched off by mistake.`}><AlertCircle className="w-[10px] h-[10px]" />AI off while engaged</span>
                : aiOff
                  ? <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[10.5px] font-semibold ring-1 ring-inset ring-slate-400/25" title={offReason ? `${offReason} — no further replies are coming` : (offLooksExpected ? `Last classified intent was ${lastIntent}, so the AI stopping is expected` : 'The AI is switched off for this lead, so no further replies are coming')}>{offReason || (offLooksExpected ? INTENT_OFF_LABELS[lastIntent] : 'SurFox stopped replying')}</span>
                : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-cyan-50 text-cyan-700 text-[10.5px] font-semibold ring-1 ring-inset ring-cyan-600/15"><img src="/logo.png" alt="" className="w-3 h-3 object-contain" />SurFox is handling</span>}
          {highlighted && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[10.5px] font-semibold ring-1 ring-inset ring-amber-600/20" title={`Mentions "${kw}"`}><Target className="w-[10px] h-[10px]" />“{kw}” mentioned</span>}
        </div>
        <p className="mt-0.5 text-[13px] text-slate-600 leading-snug truncate">Two-way conversation{rel ? ` · last reply ${rel} ago` : ''}</p>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onOpen(lead.id); }} className="shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg text-[13px] font-semibold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">View thread<ArrowRight className="w-[14px] h-[14px]" /></button>
    </div>
  );
}

function QuietRow({ entry, onOpen }) {
  const { lead, msgs } = entry;
  const last = msgs.lastMessageAt || lead.last_message_at;
  const archiveIn = Math.max(0, QUIET_ARCHIVE_DAYS - daysSince(last));
  return (
    <div onClick={() => onOpen(lead.id)} className="group flex items-center gap-3 rounded-lg bg-white border border-slate-200 px-3.5 py-2.5 hover:border-slate-300 transition-all cursor-pointer">
      <span className="grid place-items-center w-8 h-8 rounded-lg bg-slate-100 text-slate-400 shrink-0"><Clock className="w-[15px] h-[15px]" /></span>
      <span className="font-semibold text-slate-800 text-[13.5px] truncate shrink-0 max-w-[180px]">{lead.name || 'Unknown Lead'}</span>
      <WebChatBadge lead={lead} />
      <span className="text-[12.5px] text-slate-500 truncate flex-1 min-w-0">Quiet {formatRelativeTime(last)} — SurFox is auto-retrying</span>
      <span className="hidden xl:block text-[11px] text-slate-400 shrink-0 whitespace-nowrap">archives in {archiveIn}d</span>
      <button onClick={(e) => { e.stopPropagation(); onOpen(lead.id); }} className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12.5px] font-semibold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">View thread<ArrowRight className="w-[13px] h-[13px]" /></button>
    </div>
  );
}

// A lead a rep put down on purpose. The row states the snooze and gets out of the way;
// there is no wake control here yet (see the note in the main component).
function SnoozedRow({ entry, onOpen, tz }) {
  const { lead } = entry;
  const forever = isSnoozedForever(lead);
  const d = daysUntil(lead.snoozed_until);
  return (
    <div onClick={() => onOpen(lead.id)} className="group flex items-center gap-3 rounded-lg bg-white border border-slate-200 px-3.5 py-2.5 hover:border-slate-300 transition-all cursor-pointer">
      <span className="grid place-items-center w-8 h-8 rounded-lg bg-violet-50 text-violet-500 shrink-0"><Moon className="w-[15px] h-[15px]" /></span>
      <span className="font-semibold text-slate-800 text-[13.5px] truncate shrink-0 max-w-[180px]">{lead.name || 'Unknown Lead'}</span>
      <WebChatBadge lead={lead} />
      {lead.campaign && lead.campaign !== 'No Campaign' && <span className="hidden sm:block text-[12px] text-slate-400 truncate max-w-[160px] shrink-0">· {lead.campaign}</span>}
      <span className="text-[12.5px] text-slate-500 truncate flex-1 min-w-0">
        {forever
          ? <>Snoozed<span className="text-slate-400"> — SurFox is paused, no timer</span></>
          : <>Back {formatWakeTime(lead.snoozed_until, tz)}<span className="text-slate-400">{d === 0 ? ' — later today' : ` — in ${d} day${d === 1 ? '' : 's'}`}</span></>}
      </span>
      <button onClick={(e) => { e.stopPropagation(); onOpen(lead.id); }}
        className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12.5px] font-semibold bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
        View thread<ArrowRight className="w-[13px] h-[13px]" />
      </button>
    </div>
  );
}

// ============================================================================
// BENCH VIEW
// ============================================================================
function BenchView({ groups, total, onOpen }) {
  // Specialty groups are collapsible (default open). Click a group header to fold it.
  const [collapsed, setCollapsed] = useState({});
  if (total === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 text-center py-16 flex flex-col items-center">
        <img src="/transparentfoxsitting.png" alt="SurFox" className="h-28 w-auto mb-3" />
        <p className="text-slate-700 font-medium">No one to keep warm yet.</p>
        <p className="text-sm text-slate-500 mt-1">Engaged leads who aren't a fit for the current offer will land here.</p>
      </div>
    );
  }
  return (
    <div>
      <p className="text-[12px] text-slate-400 mb-3">
        Why there isn't a fit yet, and what each lead is looking for, are inferred by SurFox from every conversation.
      </p>
      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="hidden lg:grid grid-cols-[260px_160px_1fr_220px_72px] gap-3 px-4 h-10 bg-slate-50 border-b border-slate-200 items-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <span>Lead</span><span>Group</span><span>Why no fit yet</span><span>Looking for</span><span className="text-right">Engaged</span>
        </div>
        {groups.map((g, gi) => {
          const isOpen = !collapsed[g.key];
          return (
            <div key={g.key} className={gi > 0 ? 'border-t border-slate-100' : ''}>
              <button
                type="button"
                onClick={() => setCollapsed(c => ({ ...c, [g.key]: !c[g.key] }))}
                className="w-full flex items-center gap-2 px-4 py-2.5 bg-slate-50/60 hover:bg-slate-100 transition-colors text-left"
              >
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                <h4 className="text-[12.5px] font-bold text-slate-700">{g.label}</h4>
                <span className="text-[11.5px] font-semibold text-slate-400 tabular-nums">{g.people.length}</span>
              </button>
              {isOpen && (
                <div className="divide-y divide-slate-100">
                  {g.people.map(({ lead, msgs }) => (
                    <BenchRow key={lead.id} lead={lead} msgs={msgs} groupLabel={g.label} onOpen={onOpen} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BenchRow({ lead, msgs, groupLabel, onOpen }) {
  const last = msgs.lastMessageAt || lead.last_message_at;
  const fit = Array.isArray(lead.fit_for) ? lead.fit_for : [];
  return (
    <div className="group grid grid-cols-1 lg:grid-cols-[260px_160px_1fr_220px_72px] gap-3 px-4 py-3 hover:bg-slate-50 transition-colors items-center">
      <div className="flex items-center gap-2.5 min-w-0 cursor-pointer" onClick={() => onOpen(lead.id)}>
        <Avatar name={lead.name} size={32} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className="text-[13.5px] font-semibold text-slate-900 truncate">{lead.name || 'Unknown'}</p>
            <WebChatBadge lead={lead} />
          </div>
          {lead.phone && <p className="text-[11.5px] text-slate-400 truncate flex items-center gap-1"><Phone className="w-[10px] h-[10px]" />{lead.phone}</p>}
        </div>
      </div>
      <div className="min-w-0"><p className="text-[12.5px] font-medium text-slate-700 truncate">{groupLabel}</p>{lead.bench_location && <p className="text-[11px] text-slate-400 truncate">{lead.bench_location}</p>}</div>
      <div className="min-w-0">
        <p className="text-[12.5px] text-slate-600 truncate" title={lead.mismatch_details || ''}>{lead.mismatch_details || 'Engaged, not a fit for the current offer'}</p>
      </div>
      <div className="flex flex-wrap gap-1 min-w-0">
        {fit.length ? fit.map(f => <span key={f} className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-600/15">{f}</span>)
          : <span className="text-[11px] text-slate-300">—</span>}
      </div>
      <span className="text-[11.5px] text-slate-400 lg:text-right">{formatRelativeTime(last)} ago</span>
    </div>
  );
}
